import { observer } from 'mobx-react-lite';
import { style } from './BottomSheetPhotoDrawer.style';
import { Pressable, ScrollView, View, ViewStyle } from 'react-native';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import TextLato from '../text/text-lato/TextLato';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { ICON_NAMES } from '../icon/icon.helper';
import { useStore } from 'src/contexts/StoreContext';
import { useBottomSheetPhotoDrawerLogic } from './BottomSheetPhotoDrawer.logic';
import { PrimaryButton } from '../buttons/primary-button/PrimaryButton';
import { bottomSheetTestID, choosePhotosBannerTestID } from 'src/helpers/testIDs.helper';
import Button from '../buttons/button/Button';
import { TestIDs } from 'src/constants/testIDs.constants';
import { brand } from 'src/theme/colors';
import { Icon } from '../icon/Icon';
import { PhotoCard } from '../photo-card/PhotoCard';
import { PhotoDrawerSortingOptions } from '../drawer-photo-drawer/DrawerPhotoDrawer.types';
import { useEffect, useMemo } from 'react';
import { ThumbnailImageModelInstance } from 'src/store/_models/thumbnailImage';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { TextIconButton } from '../buttons/text-icon-button/TextIconButton';
import { TEXT_ICON_BUTTON_SIZES, TEXT_ICON_BUTTON_TYPES } from '../buttons/text-icon-button/TextIconButton.constants';
import { BottomSheetView } from '@gorhom/bottom-sheet';

export const BottomSheetPhotoDrawer = observer(({ bsStyle }: { bsStyle: ViewStyle }) => {
  const { i18n, bottomSheet, project, photoDrawer, dynamicModal } = useStore();
  const { dropdownSelectedOption, onPressUpload, optionCallback, onPressChooseButton } = useBottomSheetPhotoDrawerLogic(
    {
      dynamicModal,
      i18n,
      project,
      photoDrawer,
      bottomSheet,
    },
  );

  useEffect(() => {
    project.setShowNotificationPhotoDrawer(false);
  }, []);

  const onPressCard = (image: ThumbnailImageModelInstance) => {
    if (image.isSelected) {
      image.deselect();
    } else {
      image.select();
    }
  };

  const images = project.activeProject?.thumbnailImages;
  const sortedImages = useMemo(
    () =>
      images?.slice().sort((a, b) => {
        if (dropdownSelectedOption === PhotoDrawerSortingOptions.SHOT_MOST_RECENT) {
          return b.fileLastModified - a.fileLastModified;
        } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.SHOT_LESS_RECENT) {
          return a.fileLastModified - b.fileLastModified;
        } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT) {
          return b.fileAddedTimestamp - a.fileAddedTimestamp;
        } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT) {
          return a.fileAddedTimestamp - b.fileAddedTimestamp;
        } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.NAME_ALPHABETIC) {
          return a.fileName.localeCompare(b.fileName, undefined, { numeric: true });
        } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE) {
          return b.fileName.localeCompare(a.fileName, undefined, { numeric: true });
        }
        return b.usageQuantity - a.usageQuantity;
      }),
    [images, images?.length, dropdownSelectedOption],
  );

  return (
    <BottomSheetView style={bsStyle}>
      <View style={style.container}>
        <View style={style.header}>
          <Pressable
            onPress={() =>
              dynamicModal.openModal({
                dynamicModalView: DYNAMIC_MODAL_VIEWS.PhotoDrawerOrder,
                onPressOptionCallback: optionCallback,
              })
            }
          >
            <View style={{ width: 20, height: 20, padding: 2 }}>
              <Icon name={ICON_NAMES.Rearrange} color={brand.main.water} />
            </View>
          </Pressable>
          <TextLato style={style.title}>{i18n.t('photo-drawer:my-photos')}</TextLato>
          <CircularButton
            size={CIRCULAR_BUTTON_SIZES.SMALL}
            type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
            testID={bottomSheetTestID(TestIDs.CLOSE)}
            focusId={bottomSheetTestID(TestIDs.CLOSE)}
            focusGroup={FocusGroups.MODAL}
            focusOrder={1}
            iconName={ICON_NAMES.Close}
            iconStyle={style.iconClose}
            onPress={async () => {
              bottomSheet.closeBottomSheet();
              project.activeProject?.deselectAllThumbnailImages();
            }}
          />
        </View>
        {!!sortedImages?.length ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, marginHorizontal: 26, marginVertical: 16 }}
          >
            <Button
              onPress={async () => onPressUpload()}
              testID={bottomSheetTestID(TestIDs.UPLOAD_PHOTO)}
              focusId={bottomSheetTestID(TestIDs.UPLOAD_PHOTO)}
              focusOrder={2}
              focusGroup={FocusGroups.MODAL}
              accessibilityLabel={i18n.t('photo-drawer:upload')}
              style={style.uploadButton}
              innerStyle={{ height: '100%' }}
              focusStyle={style.uploadButtonFocus}
            >
              {() => {
                return (
                  <View style={style.button}>
                    <View style={style.buttonIcon}>
                      <Icon name={ICON_NAMES.Upload} color={brand.main.water} />
                    </View>
                    <TextLato style={style.buttonText}>{i18n.t('photo-drawer:upload')}</TextLato>
                  </View>
                );
              }}
            </Button>
            {sortedImages?.map((image, index) => {
              const computedWidth = (image.width * 118) / image.height;
              return (
                <View style={{ width: computedWidth, height: 118 }} key={`bottom-sheet-photo-drawer-image-${index}`}>
                  <PhotoCard
                    image={image}
                    focusId={bottomSheetTestID(TestIDs.SELECT_PHOTO, index.toString())}
                    focusGroup={FocusGroups.MODAL}
                    focusOrder={index + 3}
                    testID={bottomSheetTestID(TestIDs.SELECT_PHOTO, index.toString())}
                    onPress={() => onPressCard(image)}
                  />
                </View>
              );
            })}
          </ScrollView>
        ) : (
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: 16,
              paddingHorizontal: 16,
              gap: 16,
            }}
          >
            <View style={{ width: 48, height: 43 }}>
              <Icon name={ICON_NAMES.PhotoAlbum} color={brand.main.water} />
            </View>
            <TextLato style={[style.dropzoneText]}>{i18n.t('customize:empty-state-description-mobile')}</TextLato>
          </View>
        )}
        <View style={style.footer}>
          {sortedImages?.length ? (
            <PrimaryButton
              text={i18n.t(
                !!project.activeProject?.selectedThumbnails.length
                  ? 'photo-drawer:add-n-photos'
                  : 'photo-drawer:add-photos',
                {
                  number: project.activeProject?.selectedThumbnails.length,
                },
              )}
              textStyle={
                dynamicModal.activeModal?.isUploadingPhoto && { color: brand.main.variations['lighterWater-2'] }
              }
              testID={bottomSheetTestID(TestIDs.ADD_TO_PROJECT)}
              focusId={bottomSheetTestID(TestIDs.ADD_TO_PROJECT)}
              focusGroup={FocusGroups.MODAL}
              focusOrder={(images?.length ?? 0) + 3}
              style={style.addButton}
              onPress={async () =>
                bottomSheet.uploadButtonCallback({
                  fileIds: project.activeProject!.selectedThumbnails.map(t => t.originalImageId),
                })
              }
              disabled={!project.activeProject?.selectedThumbnails.length}
            />
          ) : (
            <TextIconButton
              onPress={onPressChooseButton}
              style={style.goToSiteContainer}
              innerStyle={style.goToSiteInnerContainer}
              accessible={true}
              accessibilityLabel={i18n.t('customize:empty-state-cta-second')}
              accessibilityRole="button"
              text={i18n.t('customize:empty-state-cta-second')}
              textStyle={[style.otherOptionsButton]}
              iconName={ICON_NAMES.ArrowRight1}
              size={TEXT_ICON_BUTTON_SIZES.SMALL}
              type={TEXT_ICON_BUTTON_TYPES.ONLY_CTA}
              testID={choosePhotosBannerTestID(TestIDs.CTA)}
              focusId={choosePhotosBannerTestID(TestIDs.CTA)}
              focusOrder={0}
              focusGroup={FocusGroups.CHOOSE_PHOTO_BANNER}
            />
          )}
        </View>
      </View>
    </BottomSheetView>
  );
});
